/**
 * Import Step 2: Column Mapping Component
 * 
 * Second step of the 3-step import process
 * Maps CSV columns to transaction fields
 */

import React, { useState, useEffect } from 'react';
import { detectBankFormat, getSuggestedMappings } from '../../utils/import-column-mapper';
import type { ColumnMapping } from '../../types/import.types';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';

interface ImportStep2ColumnMappingProps {
  csvColumns: string[];
  csvRows: string[][];
  mappings: ColumnMapping[];
  onMappingsChange: (mappings: ColumnMapping[]) => void;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
}

const MAPPABLE_FIELDS = [
  { value: 'date', label: 'Transaction Date', required: true },
  { value: 'debit', label: 'Debit Amount', required: false },
  { value: 'credit', label: 'Credit Amount', required: false },
  { value: 'amount', label: 'Amount', required: false },
  { value: 'description', label: 'Description', required: true },
  { value: 'merchant_name', label: 'Merchant Name', required: false },
  { value: 'type', label: 'Transaction Type', required: false },
  { value: 'reference_number', label: 'Reference Number', required: false },
  { value: 'external_id', label: 'External ID', required: false },
  { value: 'balance_after', label: 'Balance After', required: false },
  { value: 'cheque_number', label: 'Cheque Number', required: false }
] as const;

export function ImportStep2ColumnMapping({
  csvColumns,
  csvRows,
  mappings,
  onMappingsChange,
  onNext,
  onBack,
  onCancel
}: ImportStep2ColumnMappingProps): React.ReactElement {
  const [detectedBank, setDetectedBank] = useState<string>('Unknown');
  const [showPreview, setShowPreview] = useState<boolean>(false);

  // Check if CSV has debit/credit columns or amount column
  const hasDebitColumn = csvColumns.some(col => col.toLowerCase().trim() === 'debit');
  const hasCreditColumn = csvColumns.some(col => col.toLowerCase().trim() === 'credit');
  const hasDebitOrCredit = hasDebitColumn || hasCreditColumn;

  useEffect(() => {
    // Detect bank format and suggest mappings
    const bankFormat = detectBankFormat(csvColumns);
    setDetectedBank(bankFormat);

    if (bankFormat !== 'Unknown' && mappings.length === 0) {
      const suggested = getSuggestedMappings(bankFormat, csvColumns);
      // Update required status based on available columns
      const updatedMappings = suggested.map(mapping => {
        // If debit/credit columns exist, make them required
        if (hasDebitOrCredit && (mapping.mappedField === 'debit' || mapping.mappedField === 'credit')) {
          return { ...mapping, isRequired: true };
        }
        // If only amount column exists, make it required
        if (!hasDebitOrCredit && mapping.mappedField === 'amount') {
          return { ...mapping, isRequired: true };
        }
        return mapping;
      });
      onMappingsChange(updatedMappings);
    }
  }, [csvColumns, mappings.length, onMappingsChange, hasDebitOrCredit]);

  const handleMappingChange = (csvColumn: string, mappedField: string) => {
    const updatedMappings = mappings.filter(m => m.csvColumn !== csvColumn);
    
    // Determine if this field should be required
    let isRequired = false;
    if (mappedField === 'date' || mappedField === 'description') {
      // Date and description are always required
      isRequired = true;
    } else if (mappedField === 'debit' || mappedField === 'credit') {
      // If debit/credit columns exist in CSV, they are required
      isRequired = hasDebitOrCredit;
    } else if (mappedField === 'amount') {
      // If only amount column exists (no debit/credit), it is required
      isRequired = !hasDebitOrCredit;
    }
    
    // Validate mutual exclusivity: cannot map both amount and debit/credit
    if (mappedField === 'amount') {
      // If mapping to amount, remove any existing debit/credit mappings
      const filteredMappings = updatedMappings.filter(m => 
        m.mappedField !== 'debit' && m.mappedField !== 'credit'
      );
      filteredMappings.push({ csvColumn, mappedField: mappedField as any, isRequired });
      onMappingsChange(filteredMappings);
    } else if (mappedField === 'debit' || mappedField === 'credit') {
      // If mapping to debit/credit, remove any existing amount mapping
      const filteredMappings = updatedMappings.filter(m => m.mappedField !== 'amount');
      filteredMappings.push({ csvColumn, mappedField: mappedField as any, isRequired });
      onMappingsChange(filteredMappings);
    } else {
      // For other fields, just add the mapping
      if (mappedField) {
        updatedMappings.push({ csvColumn, mappedField: mappedField as any, isRequired });
      }
      onMappingsChange(updatedMappings);
    }
  };

  const isMappingValid = () => {
    // Check required fields are mapped
    const hasDate = mappings.some(m => m.mappedField === 'date');
    const hasDescription = mappings.some(m => m.mappedField === 'description');
    
    // Check for amount OR (debit/credit) - mutual exclusivity
    const hasAmount = mappings.some(m => m.mappedField === 'amount');
    const hasDebit = mappings.some(m => m.mappedField === 'debit');
    const hasCredit = mappings.some(m => m.mappedField === 'credit');
    const hasDebitCredit = hasDebit || hasCredit;
    
    // Validate mutual exclusivity: cannot have both amount and debit/credit
    const hasConflict = hasAmount && hasDebitCredit;
    
    // If debit/credit columns exist, at least one must be mapped
    // If only amount column exists, it must be mapped
    let hasValidAmountMapping = false;
    if (hasDebitOrCredit) {
      hasValidAmountMapping = hasDebitCredit;
    } else {
      hasValidAmountMapping = hasAmount;
    }
    
    return hasDate && hasDescription && hasValidAmountMapping && !hasConflict;
  };

  // Get first 5 rows for preview
  const previewRows = csvRows.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Bank Format Detection */}
      {detectedBank !== 'Unknown' && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <Check className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                Bank format detected: <strong>{detectedBank}</strong>
              </p>
              <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                Column mappings have been automatically suggested based on the detected format.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CSV Data Preview */}
      {previewRows.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="w-full px-6 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="text-left">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  CSV Data Preview (First 5 Rows)
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Use this preview to help map your columns correctly
                </p>
              </div>
              <ChevronDown
                className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform flex-shrink-0 ${
                  showPreview ? 'rotate-180' : ''
                }`}
                aria-hidden="true"
              />
            </div>
          </button>
          {showPreview && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {csvColumns.map((column, index) => (
                      <th
                        key={index}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600 last:border-r-0"
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {previewRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      {csvColumns.map((_column, colIndex) => (
                        <td
                          key={colIndex}
                          className="px-4 py-3 text-xs text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-600 last:border-r-0 max-w-xs truncate"
                          title={row[colIndex] || ''}
                        >
                          {row[colIndex] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Amount Column Help Text */}
      {mappings.some(m => m.mappedField === 'amount') && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <strong>Amount Column Selected:</strong> When using a single Amount column, the sign determines the transaction type. 
            You can control how positive/negative values are interpreted in Step 3 (Import Settings).
          </p>
        </div>
      )}

      {/* Column Mapping Table */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Column Mapping
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Map each CSV column to the corresponding transaction field. Required fields are marked with an asterisk (*).
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  CSV Column
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Map To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Required
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {csvColumns.map((column) => {
                const currentMapping = mappings.find(m => m.csvColumn === column);
                return (
                  <tr key={column}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {column}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={currentMapping?.mappedField || ''}
                        onChange={(e) => {
                          handleMappingChange(column, e.target.value);
                        }}
                        className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 py-2 px-3 text-sm"
                      >
                        <option value="">-- Select Field --</option>
                        {MAPPABLE_FIELDS.map((field) => (
                          <option key={field.value} value={field.value}>
                            {field.label} {field.required && '*'}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {currentMapping?.isRequired ? (
                        <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded text-xs font-medium">
                          Required
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">Optional</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Validation Message */}
      {!isMappingValid() && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            {mappings.some(m => m.mappedField === 'amount') && mappings.some(m => ['debit', 'credit'].includes(m.mappedField))
              ? 'Cannot map both Amount column and Debit/Credit columns. Please use either Amount OR Debit/Credit columns.'
              : 'Please map the required fields: Date, Description, and Amount (or Debit/Credit columns)'}
          </p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 font-medium"
        >
          Back
        </button>
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onNext}
            disabled={!isMappingValid()}
            className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
          >
            <span>Next: Review</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

